import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { useToast } from '../components/Toast';
import {
  Listing,
  ListingStatus,
  LeaseTerm,
  PetPolicy,
  getListing,
  listingTypeLabel,
  setListingStatus,
} from '../api/listings';
import { extractApiError } from '../api/legalServices';

/** Strip the trailing /api so relatively-stored photo paths still resolve. */
const API_ORIGIN = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'
).replace(/\/api\/?$/, '');

const resolvePhoto = (u: string): string =>
  /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`;

const petPolicyLabel: Record<PetPolicy, string> = {
  allowed: 'Pet Friendly',
  'not-allowed': 'No Pets',
  'cats-only': 'Cats Only',
  'dogs-only': 'Dogs Only',
};

const leaseTermLabel: Record<LeaseTerm, string> = {
  'month-to-month': 'Month-to-month',
  '6-months': '6 months',
  '1-year': '1 year',
  '2-years': '2 years',
};

const leaseMonths = (term: LeaseTerm | null): string => {
  switch (term) {
    case '6-months':
      return '6';
    case '1-year':
      return '12';
    case '2-years':
      return '24';
    case 'month-to-month':
      return 'M2M';
    default:
      return '—';
  }
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPrice = (price: Listing['price'], type: Listing['type']): string => {
  const n = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(n)) return '—';
  const formatted = '$' + n.toLocaleString();
  return type === 'Rental' ? `${formatted}/mo` : formatted;
};

const initials = (name?: string): string =>
  (name || '?')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const StatTile: React.FC<{ icon: string; value: React.ReactNode; label: string }> = ({
  icon,
  value,
  label,
}) => (
  <div
    className="min-w-0 rounded-2xl flex flex-col items-center justify-center py-3.5 px-1"
    style={{ background: 'var(--g50)' }}
  >
    <Icon name={icon} size={20} color="var(--brand)" />
    <div className="text-[17px] font-extrabold text-ink mt-1.5 leading-none truncate max-w-full">
      {value}
    </div>
    <div className="text-[11px] text-g500 mt-1 whitespace-nowrap">{label}</div>
  </div>
);

interface ListingDetailProps {
  /**
   * Route prefix this page lives under, so "Back to listings" and the error
   * fallback return to the right place. '/admin' for the admin console,
   * '/agent' for the agent panel.
   */
  basePath?: '/admin' | '/agent';
}

/**
 * Where "Back" goes when another page opened this listing, e.g. a merchant's
 * detail page passes { backTo: '/admin/agents/13', backLabel: 'Back to merchant' }.
 */
export interface ListingBackState {
  backTo?: string;
  backLabel?: string;
}

const ListingDetail: React.FC<ListingDetailProps> = ({ basePath = '/admin' }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const listingsPath = `${basePath}/listings`;
  // Only follow a back link that stays inside this console.
  const back = (location.state as ListingBackState | null) ?? {};
  const backPath = back.backTo?.startsWith(`${basePath}/`) ? back.backTo : listingsPath;
  const backLabel = backPath === listingsPath ? 'Back to listings' : back.backLabel || 'Back';

  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getListing(Number(id));
      setListing(res);
    } catch (err) {
      setError(extractApiError(err, 'Failed to load listing'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = async (next: ListingStatus) => {
    if (!listing) return;
    setBusy(true);
    try {
      const updated = await setListingStatus(listing.id, next);
      setListing((prev) => (prev ? { ...prev, ...updated } : prev));
      toast(
        `"${updated.title}" ${next === 'active' ? 'activated' : 'deactivated'}`,
        next === 'active' ? 'success' : 'info'
      );
    } catch (err) {
      toast(extractApiError(err, 'Failed to update listing'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="building-2" size={28} color="var(--g400)" /> Loading listing...
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="card p-6 max-w-[520px]">
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--red)' }}>
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load this listing</strong>
        </div>
        <div className="text-sm text-g600 mb-4">{error || 'Listing not found.'}</div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate(backPath)}
          >
            {backLabel}
          </button>
        </div>
      </div>
    );
  }

  const photos = (listing.photos && listing.photos.length
    ? listing.photos
    : listing.coverPhoto
    ? [listing.coverPhoto]
    : []
  ).map(resolvePhoto);
  const hero = photos[activePhoto];

  const amenities = Array.isArray(listing.amenities) ? listing.amenities : [];
  const chips: string[] = [...amenities];
  if (listing.petPolicy) chips.push(petPolicyLabel[listing.petPolicy]);
  if (listing.leaseTerm) chips.push(leaseTermLabel[listing.leaseTerm]);

  return (
    <div className="animate-fadeIn">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 sm:mb-6">
        <button
          className="flex items-center gap-2 text-sm font-semibold text-g600 hover:text-ink transition-colors"
          onClick={() => navigate(backPath)}
        >
          <Icon name="arrow-left" size={18} /> {backLabel}
        </button>
        <div className="flex items-center gap-2.5">
          <span
            className="px-3 py-1.5 rounded-full text-[12px] font-semibold"
            style={{
              background: listing.status === 'active' ? 'var(--green-bg)' : 'var(--g100)',
              color: listing.status === 'active' ? 'var(--green)' : 'var(--g500)',
            }}
          >
            {listing.status === 'active' ? 'Active' : 'Inactive'}
          </span>
          {listing.status === 'active' ? (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => toggleStatus('inactive')}
              disabled={busy}
            >
              <Icon name="x-circle" size={14} /> Deactivate
            </button>
          ) : (
            <button
              className="btn btn-success btn-sm"
              onClick={() => toggleStatus('active')}
              disabled={busy}
            >
              <Icon name="check-circle" size={14} color="#fff" /> Activate
            </button>
          )}
        </div>
      </div>

      {/* Two-column body */}
      <div className="flex gap-4 sm:gap-6 flex-wrap items-start">
        {/* ─── Left: gallery ─── */}
        <div className="w-full min-w-0 lg:w-auto lg:flex-1 lg:min-w-[320px]">
          <div
            className="rounded-card overflow-hidden border border-g100 relative"
            style={{ aspectRatio: '4 / 3', background: 'var(--g100)' }}
          >
            {hero ? (
              <img src={hero} alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,var(--brand-light),var(--g100))' }}
              >
                <Icon name="building-2" size={56} color="var(--g400)" />
              </div>
            )}
            {photos.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setActivePhoto((i) => (i - 1 + photos.length) % photos.length)
                  }
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.9)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                >
                  <Icon name="chevron-right" size={18} className="rotate-180" />
                </button>
                <button
                  onClick={() => setActivePhoto((i) => (i + 1) % photos.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.9)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                >
                  <Icon name="chevron-right" size={18} />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {photos.length > 1 && (
            <div className="flex gap-2.5 mt-3 flex-wrap">
              {photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setActivePhoto(i)}
                  className="rounded-xl overflow-hidden flex-shrink-0 w-[68px] h-[52px] sm:w-[84px] sm:h-16"
                  style={{
                    border: '2px solid ' + (i === activePhoto ? 'var(--brand)' : 'transparent'),
                  }}
                >
                  <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* About */}
          <div className="card p-4 sm:p-6 mt-4 sm:mt-6">
            <div className="text-[17px] font-bold text-ink mb-2.5">About This Property</div>
            <p className="text-[14px] text-g600 leading-relaxed whitespace-pre-line">
              {listing.description || 'No description provided.'}
            </p>
          </div>

          {/* Amenities & Policies */}
          {chips.length > 0 && (
            <div className="card p-4 sm:p-6 mt-4 sm:mt-6">
              <div className="text-[17px] font-bold text-ink mb-3">Amenities &amp; Policies</div>
              <div className="flex flex-wrap gap-2">
                {chips.map((c, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium"
                    style={{ background: 'var(--g50)', color: 'var(--g700)' }}
                  >
                    <Icon name="check-circle" size={14} color="var(--brand)" /> {c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ─── Right: summary ─── */}
        <div className="w-full lg:w-[360px] lg:flex-shrink-0 flex flex-col gap-4 sm:gap-6">
          <div className="card p-4 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="text-[26px] sm:text-[30px] font-extrabold text-ink leading-none min-w-0 break-words">
                {formatPrice(listing.price, listing.type)}
              </div>
              <span
                className="px-3 py-1 rounded-full text-[12px] font-semibold whitespace-nowrap"
                style={{ background: 'var(--brand-light)', color: 'var(--brand)' }}
              >
                {listing.type === 'Rental' ? 'For Rent' : listingTypeLabel(listing.type)}
              </span>
            </div>
            <div className="text-[15px] font-semibold text-ink mt-3">{listing.title}</div>
            <div className="text-[13px] text-g500 mt-1 flex items-start gap-1.5">
              <Icon name="map-pin" size={14} color="var(--g400)" className="flex-shrink-0 mt-0.5" />
              <span className="min-w-0 break-words">{listing.address}</span>
            </div>

            <div className="grid grid-cols-4 gap-1.5 sm:gap-2 mt-5">
              <StatTile icon="bed" value={listing.beds} label="Beds" />
              <StatTile icon="bath" value={listing.baths} label="Baths" />
              <StatTile
                icon="maximize"
                value={listing.sqft ? listing.sqft.toLocaleString() : '—'}
                label="Sqft"
              />
              <StatTile icon="calendar" value={leaseMonths(listing.leaseTerm)} label="Mo Lease" />
            </div>
          </div>

          {/* Details meta */}
          <div className="card p-4 sm:p-6">
            <div className="text-[17px] font-bold text-ink mb-3">Details</div>
            <div className="grid grid-cols-2 gap-y-2.5 text-[13px]">
              <div className="text-g500">Type</div>
              <div className="text-ink font-medium text-right">{listingTypeLabel(listing.type)}</div>
              <div className="text-g500">Lease term</div>
              <div className="text-ink font-medium text-right">
                {listing.leaseTerm ? leaseTermLabel[listing.leaseTerm] : '—'}
              </div>
              <div className="text-g500">Available from</div>
              <div className="text-ink font-medium text-right">
                {formatDate(listing.availableFrom)}
              </div>
              <div className="text-g500">Listed on</div>
              <div className="text-ink font-medium text-right">{formatDate(listing.createdAt)}</div>
              <div className="text-g500">Last updated</div>
              <div className="text-ink font-medium text-right">{formatDate(listing.updatedAt)}</div>
              <div className="text-g500">Status</div>
              <div className="text-ink font-medium text-right capitalize">{listing.status}</div>
              <div className="text-g500">Listing ID</div>
              <div className="text-ink font-medium text-right">#{listing.id}</div>
            </div>
          </div>

          {/* Listed By */}
          <div className="card p-4 sm:p-6">
            <div className="text-[17px] font-bold text-ink mb-3">Listed By</div>
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-[15px] font-bold flex-shrink-0"
                style={{ background: 'var(--brand-light)', color: 'var(--brand)' }}
              >
                {initials(listing.agent?.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-ink truncate">
                  {listing.agent?.name || 'Unknown merchant'}
                </div>
                <div className="text-[12px] text-g500 truncate">Property Merchant · Rentagade</div>
                {listing.agent?.email && (
                  <div className="text-[12px] text-g500 truncate">{listing.agent.email}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetail;
